import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";

// ---------- Types ----------
export interface CleaningGuestRating {
  guestId: string;
  reservationId?: string;
  listingRating: number;
  dateCreated: string;
  cleanliness: number;
  location: number;
  communication: number;
  accuracy: number;
  service: number;
  overallRating:
    | "Excellent"
    | "Superb"
    | "Very Nice"
    | "Worth It"
    | "Not Worth It";
  guestReview: string;
  checkInRating?: number;
  communicationGeneral?: number;
  value?: number;
}

export interface CleaningRatingSummary {
  avgRating: number;
  cleanliness: number;
  location: number;
  communication: number;
  accuracy: number;
  service: number;
  value: number;
  totalReviews: number;
  lastUpdated: string;
}

export interface CleaningRatingState {
  ratings: Record<string, CleaningGuestRating[]>; // keyed by listingId
  loading: boolean;
  error: string | null;
  reviewed: boolean;
  currentReview?: CleaningGuestRating | null;
  summaries: Record<string, CleaningRatingSummary | null>;
}

const initialState: CleaningRatingState = {
  ratings: {},
  loading: false,
  error: null,
  reviewed: false,
  currentReview: null,
  summaries: {},
};

// ---------- Submit a Rating (includes summary updates) ----------
export const submitCleaningRating = createAsyncThunk(
  "cleaningRatings/submitCleaningRating",
  async (
    {
      listingId,
      hostId,
      reservationId,
      guestId,
      reviewText,
      overallRating,
      criteria,
      listingRating,
    }: {
      listingId: string;
      hostId: string;
      reservationId: string;
      guestId: string;
      reviewText: string;
      overallRating: string;
      listingRating: number;
      criteria: Record<string, number>;
    },
    { rejectWithValue }
  ) => {
    try {
      // 1️⃣ Prepare guest review doc reference
      const guestRef = doc(db, "cleaningratings", listingId, "ratings", guestId);
      const guestSnap = await getDoc(guestRef);

      const newRating: CleaningGuestRating = {
        reservationId,
        listingRating,
        guestId,
        guestReview: reviewText,
        overallRating: overallRating as CleaningGuestRating["overallRating"],
        cleanliness:           criteria["Cleanliness"]        || 0,
        location:              criteria["Location"]           || 0,
        communication:         criteria["Host Communication"] || 0,
        accuracy:              criteria["Accuracy"]           || 0,
        service:               criteria["Host Service"]       || 0,
        checkInRating:         criteria["Check-in"]          || 0,
        communicationGeneral:  criteria["Communication"]      || 0,
        value:                 criteria["Value"]              || 0,
        dateCreated: new Date().toISOString(),
      };

      // 2️⃣ Save rating under guest
      if (guestSnap.exists()) {
        const existing = guestSnap.data();
        const ratings: CleaningGuestRating[] = existing.ratings || [];
        const alreadyReviewed = ratings.some(
          (r) => r.reservationId === reservationId
        );
        if (alreadyReviewed) return { reviewed: true, listingId };

        await updateDoc(guestRef, {
          ratings: [...ratings, newRating],
          lastUpdated: Timestamp.now(),
        });
      } else {
        await setDoc(guestRef, {
          guestId,
          ratings: [newRating],
          createdAt: Timestamp.now(),
        });
      }

      // 3️⃣ Update summary for listing (incremental)
      const listingRef = doc(db, "cleaningratings", listingId);
      const listingSnap = await getDoc(listingRef);

      const summary: CleaningRatingSummary = listingSnap.data()?.summary || {
        avgRating: 0,
        cleanliness: 0,
        location: 0,
        communication: 0,
        accuracy: 0,
        service: 0,
        value: 0,
        totalReviews: 0,
        lastUpdated: new Date().toISOString(),
      };

      const n = summary.totalReviews;
      const newN = n + 1;

      const updatedSummary: CleaningRatingSummary = {
        avgRating:     (summary.avgRating     * n + (newRating.listingRating ?? 0)) / newN,
        cleanliness:   (summary.cleanliness   * n + (newRating.cleanliness   ?? 0)) / newN,
        location:      (summary.location      * n + (newRating.location      ?? 0)) / newN,
        communication: (summary.communication * n + (newRating.communication ?? 0)) / newN,
        accuracy:      (summary.accuracy      * n + (newRating.accuracy      ?? 0)) / newN,
        service:       (summary.service       * n + (newRating.service       ?? 0)) / newN,
        value:         (summary.value         * n + (newRating.value         ?? 0)) / newN,
        totalReviews: newN,
        lastUpdated: new Date().toISOString(),
      };

      // 4️⃣ Merge summary + ensure listing root exists
      if (listingSnap.exists()) {
        await updateDoc(listingRef, { summary: updatedSummary });
      } else {
        await setDoc(listingRef, {
          listingId,
          hostId,
          summary: updatedSummary,
          createdAt: Timestamp.now(),
        });
      }

      return {
        reviewed: false,
        listingId,
        guestId,
        rating: newRating,
        summary: updatedSummary,
      };
    } catch (err: any) {
      console.error("🔥 submitCleaningRating error:", err);
      return rejectWithValue(err.message);
    }
  }
);

// ---------- Fetch all ratings for a listing ----------
export const fetchCleaningRatingsByListing = createAsyncThunk<
  { listingId: string; ratings: CleaningGuestRating[] },
  string,
  { rejectValue: string }
>("cleaningRatings/fetchByListing", async (listingId, { rejectWithValue }) => {
  try {
    const ratingsCol = collection(db, "cleaningratings", listingId, "ratings");
    const snapshot = await getDocs(ratingsCol);

    const allRatings: CleaningGuestRating[] = [];
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      if (data.ratings) allRatings.push(...data.ratings);
    });

    return { listingId, ratings: allRatings };
  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});

// ---------- Fetch ratings for a specific guest ----------
export const fetchCleaningGuestRatings = createAsyncThunk<
  { listingId: string; guestId: string; ratings: CleaningGuestRating[] },
  { listingId: string; guestId: string },
  { rejectValue: string }
>("cleaningRatings/fetchGuestRatings", async ({ listingId, guestId }, { rejectWithValue }) => {
  try {
    const docRef = doc(db, "cleaningratings", listingId, "ratings", guestId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return { listingId, guestId, ratings: [] };
    return { listingId, guestId, ratings: snap.data().ratings || [] };
  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});

// ---------- Fetch rating by reservationId ----------
export const fetchCleaningRatingByReservation = createAsyncThunk<
  CleaningGuestRating | null,
  { listingId: string; reservationId: string },
  { rejectValue: string }
>("cleaningRatings/fetchByReservation", async ({ listingId, reservationId }, { rejectWithValue }) => {
  try {
    const ratingsCol = collection(db, "cleaningratings", listingId, "ratings");
    const snapshot = await getDocs(ratingsCol);

    for (const docSnap of snapshot.docs) {
      const data = docSnap.data();
      const match = data.ratings?.find(
        (r: any) => r.reservationId === reservationId
      );
      if (match) return match;
    }

    return null;
  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});

// ---------- Fetch all ratings across listings ----------
export const fetchAllCleaningRatings = createAsyncThunk(
  "cleaningRatings/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const listingsSnap = await getDocs(collection(db, "cleaningratings"));
      const allRatings: Record<string, CleaningGuestRating[]> = {};

      for (const listingDoc of listingsSnap.docs) {
        const listingId = listingDoc.id;
        const ratingsCol = collection(db, "cleaningratings", listingId, "ratings");
        const ratingDocs = await getDocs(ratingsCol);

        const listingRatings: CleaningGuestRating[] = [];
        ratingDocs.forEach((rDoc) => {
          const data = rDoc.data();
          if (data.ratings) listingRatings.push(...data.ratings);
        });

        allRatings[listingId] = listingRatings;
      }

      return allRatings;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

// ---------- Fetch all summaries ----------
export const fetchAllCleaningRatingSummaries = createAsyncThunk<
  Record<string, CleaningRatingSummary | null>,
  void,
  { rejectValue: string }
>("cleaningRatings/fetchAllSummaries", async (_, { rejectWithValue }) => {
  try {
    const listingsSnap = await getDocs(collection(db, "cleaningratings"));
    const summaries: Record<string, CleaningRatingSummary | null> = {};

    listingsSnap.forEach((docSnap) => {
      const data = docSnap.data();
      summaries[docSnap.id] = data.summary || null;
    });

    return summaries;
  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});

// ---------- Slice ----------
const cleaningRatingSlice = createSlice({
  name: "cleaningRatings",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Submit
      .addCase(submitCleaningRating.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(submitCleaningRating.fulfilled, (state, action) => {
        const { listingId, rating } = action.payload;
        if (!state.ratings[listingId]) state.ratings[listingId] = [];
        if (rating) state.ratings[listingId].push(rating);
        state.loading = false;
      })
      .addCase(submitCleaningRating.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      })

      // Fetch listing ratings
      .addCase(fetchCleaningRatingsByListing.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCleaningRatingsByListing.fulfilled, (state, action) => {
        state.ratings[action.payload.listingId] = action.payload.ratings;
        state.loading = false;
      })
      .addCase(fetchCleaningRatingsByListing.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      })

      // Fetch guest-specific
      .addCase(fetchCleaningGuestRatings.fulfilled, (state, action) => {
        state.ratings[action.payload.listingId] = action.payload.ratings;
      })

      // Fetch by reservation
      .addCase(fetchCleaningRatingByReservation.fulfilled, (state, action) => {
        state.currentReview = action.payload;
      })

      // Fetch all
      .addCase(fetchAllCleaningRatings.fulfilled, (state, action) => {
        state.ratings = action.payload;
      })

      // Fetch all summaries
      .addCase(fetchAllCleaningRatingSummaries.fulfilled, (state, action) => {
        state.summaries = action.payload;
      });
  },
});

export default cleaningRatingSlice.reducer;
